import React, { useId, useMemo, useCallback } from "react";
import { useTheme } from "./ThemeProvider";

/**
 * Bootstrap-only Theme Selector
 * Props:
 * - size: 'sm' | 'md' | 'lg'  (default 'md')
 * - labels: boolean            (default true)
 * - className: string
 */
export default function ThemeSelector({ size = "md", labels = true, className = "" }) {
  const { theme, setTheme, systemPref } = useTheme();
  const gid = useId();

  const OPTIONS = useMemo(() => ([
    { value: "system", label: "System", icon: "bi-laptop", hint: systemPref ? `Follows OS (${systemPref})` : "Follows OS" },
    { value: "light",  label: "Light",  icon: "bi-brightness-high", hint: "Bright background" },
    { value: "dark",   label: "Dark",   icon: "bi-moon-stars", hint: "Dim background" },
  ]), [systemPref]);

  const sizeClass = size === "lg" ? "btn-lg" : size === "sm" ? "btn-sm" : "";

  const onKeyDown = useCallback((e, idx) => {
    if (!["ArrowLeft", "ArrowRight"].includes(e.key)) return;
    e.preventDefault();
    const next = e.key === "ArrowRight" ? idx + 1 : idx - 1;
    const safe = (next + OPTIONS.length) % OPTIONS.length;
    setTheme(OPTIONS[safe].value);
  }, [OPTIONS, setTheme]);

  const Swatch = ({ mode }) => {
    const box = { width: 72, height: 40, borderRadius: ".5rem" };
    if (mode === "system") {
      return (
        <div className="d-flex" style={{ gap: 4 }}>
          <div className="bg-light border flex-fill position-relative" style={box}>
            <div className="bg-body-secondary rounded-pill position-absolute" style={{ left: 8, right: 8, top: 8, height: 8 }} />
            <div className="bg-primary rounded-circle position-absolute" style={{ right: 8, bottom: 8, width: 12, height: 12 }} />
          </div>
          <div className="bg-dark border flex-fill position-relative" style={box}>
            <div className="bg-secondary rounded-pill position-absolute" style={{ left: 8, right: 8, top: 8, height: 8, opacity: .9 }} />
            <div className="bg-info rounded-circle position-absolute" style={{ right: 8, bottom: 8, width: 12, height: 12 }} />
          </div>
        </div>
      );
    }
    if (mode === "dark") {
      return (
        <div className="bg-dark border position-relative" style={box}>
          <div className="bg-secondary rounded-pill position-absolute" style={{ left: 8, right: 8, top: 8, height: 8, opacity: .9 }} />
          <div className="bg-info rounded-circle position-absolute" style={{ right: 8, bottom: 8, width: 12, height: 12 }} />
        </div>
      );
    }
    return (
      <div className="bg-light border position-relative" style={box}>
        <div className="bg-body-secondary rounded-pill position-absolute" style={{ left: 8, right: 8, top: 8, height: 8 }} />
        <div className="bg-primary rounded-circle position-absolute" style={{ right: 8, bottom: 8, width: 12, height: 12 }} />
      </div>
    );
  };

  return (
    <div className={`d-grid gap-2 ${className}`} style={{ gridTemplateColumns: "repeat(3, minmax(0,1fr))" }} role="radiogroup" aria-label="Theme selector">
      {OPTIONS.map((opt, i) => {
        const id = `${gid}-${opt.value}`;
        const active = theme === opt.value;
        return (
          <div key={opt.value} className="d-flex">
            <input
              type="radio"
              className="btn-check"
              name={`theme-${gid}`}
              id={id}
              autoComplete="off"
              checked={active}
              onChange={() => setTheme(opt.value)}
              onKeyDown={(e) => onKeyDown(e, i)}
              aria-checked={active}
            />
            <label
              className={[
                "btn w-100 text-start border rounded-4 shadow-sm",
                sizeClass,
                active ? "btn-outline-primary border-primary" : "btn-outline-secondary"
              ].join(" ")}
              htmlFor={id}
              title={opt.hint}
              aria-pressed={active}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <i className={`bi ${opt.icon}`} aria-hidden="true" />
                  {labels && <span className="fw-semibold">{opt.label}</span>}
                </div>
              </div>

              <div className="mt-2">
                <Swatch mode={opt.value} />
              </div>
            </label>
          </div>
        );
      })}
    </div>
  );
}
